import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { OverlayGrayBackground } from './OverlayGrayBackground';
import { LoaderSpinner } from './LoaderSpinner';

const formatUnitCost = (value) => {
  const cost = Number(value);
  return Number.isFinite(cost) ? `$${cost.toFixed(2)}` : '—';
};

const ProductSelectorModal = ({
  isOpen,
  onClose,
  selectedIds = [],
  onConfirm,
  groups = [],
  loading = false,
  subtitle = 'Select products for your purchase order',
  showUnitCost = true,
}) => {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelected(selectedIds);
  }, [isOpen, selectedIds]);

  const toggleGroup = (group) => {
    const variantIds = group.variants.map(v => v.id);
    const allSelected = variantIds.every(id => selected.includes(id));
    if (allSelected) {
      setSelected(prev => prev.filter(id => !variantIds.includes(id)));
    } else {
      setSelected(prev => Array.from(new Set([...prev, ...variantIds])));
    }
  };

  const toggleVariant = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    const selectedVariants = [];
    groups.forEach(g => {
      g.variants.forEach(v => {
        if (selected.includes(v.id)) {
          selectedVariants.push({
            id: v.id,
            name: v.name,
            sku: v.sku,
            unitCost: v.unitCost,
            materials: g.materials,
            image: v.image || g.image || null,
            groupId: v.groupId
          });
        }
      });
    });
    onConfirm(selectedVariants);
    onClose();
  };

  const searchLower = search.toLowerCase();
  const filteredGroups = useMemo(() => {
    return groups
      .map(g => {
        if (!searchLower) return g;
        if (g.name.toLowerCase().includes(searchLower)) return g;
        const variants = g.variants.filter(v =>
          `${g.name} - ${v.name}`.toLowerCase().includes(searchLower) ||
          v.name.toLowerCase().includes(searchLower) ||
          v.sku.toLowerCase().includes(searchLower)
        );
        return variants.length ? { ...g, variants } : null;
      })
      .filter(Boolean);
  }, [groups, searchLower]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" onClick={onClose}></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
        <div className="inline-flex w-full max-h-[90vh] transform flex-col overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-5xl sm:align-middle" onClick={e => e.stopPropagation()}>
          <div className="shrink-0 p-5 pb-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold leading-6 text-gray-900">Add Products</h3>
                <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <span className="text-xl leading-none">×</span>
              </button>
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products"
              className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </div>
          <div className="relative mx-5 mb-3 min-h-0 flex-1 overflow-hidden rounded-md border border-gray-200">
              {loading && (
                <>
                  <OverlayGrayBackground />
                  <div className="absolute inset-0 flex items-center justify-center z-40">
                    <LoaderSpinner width="40" height="40" border="3" colorSpinner="black" />
                  </div>
                </>
              )}
            <div className="hidden h-full overflow-y-auto sm:block">
              <table className="w-full table-fixed text-sm">
                <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-12 px-3 py-2" aria-label="Select" />
                    <th className="w-14 px-2 py-2" aria-label="Image" />
                    <th className="w-40 px-2 py-2">SKU</th>
                    <th className="px-2 py-2">Canonical Name</th>
                    {showUnitCost && <th className="w-28 px-3 py-2 text-right">Unit Cost</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredGroups.map(group => (
                    <React.Fragment key={group.id}>
                      <tr className="cursor-pointer bg-gray-50 hover:bg-gray-100" onClick={() => toggleGroup(group)}>
                        <td colSpan={showUnitCost ? 5 : 4} className="px-3 py-2">
                          <div className="flex items-center gap-2 font-semibold text-gray-900">
                            <input
                              type="checkbox"
                              checked={group.variants.every(v => selected.includes(v.id))}
                              onChange={() => toggleGroup(group)}
                              onClick={e => e.stopPropagation()}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              aria-label={`Select all ${group.name}`}
                            />
                            {group.image ? (
                              <img
                                src={group.image}
                                alt=""
                                className="h-8 w-8 shrink-0 rounded object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=+';
                                }}
                              />
                            ) : (
                              <div className="h-8 w-8 shrink-0 rounded border border-gray-200 bg-white" />
                            )}
                            <span className="truncate" title={group.name}>{group.name}</span>
                            <span className="ml-auto text-xs font-normal text-gray-500">{group.variants.length} variants</span>
                          </div>
                        </td>
                      </tr>
                      {group.variants.map(variant => {
                        const canonicalName = `${group.name} - ${variant.name}`;
                        return (
                          <tr
                            key={variant.id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => toggleVariant(variant.id)}
                          >
                            <td className="px-3 py-2 pl-7">
                              <input
                                type="checkbox"
                                checked={selected.includes(variant.id)}
                                onChange={() => toggleVariant(variant.id)}
                                onClick={e => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                aria-label={`Select ${canonicalName}`}
                              />
                            </td>
                            <td className="px-2 py-2">
                              {variant.image || group.image ? (
                                <img src={variant.image || group.image} alt="" className="h-8 w-8 rounded object-contain" />
                              ) : (
                                <div className="h-8 w-8 rounded border border-gray-200 bg-gray-50" />
                              )}
                            </td>
                            <td className="w-40 max-w-0 px-2 py-2">
                              <span className="block max-w-full truncate rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800" title={variant.sku}>
                                {variant.sku}
                              </span>
                            </td>
                            <td className="max-w-0 px-2 py-2">
                              <span className="block truncate text-gray-900" title={canonicalName}>{canonicalName}</span>
                            </td>
                            {showUnitCost && <td className="w-28 whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-gray-700">{formatUnitCost(variant.unitCost)}</td>}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="h-full overflow-y-auto p-2 sm:hidden">
              {filteredGroups.map(group => (
                <div key={group.id} className="mb-3 overflow-hidden rounded-md border border-gray-200 last:mb-0">
                  <div className="flex cursor-pointer items-center gap-2 bg-gray-50 p-2" onClick={() => toggleGroup(group)}>
                    <input
                      type="checkbox"
                      checked={group.variants.every(v => selected.includes(v.id))}
                      onChange={() => toggleGroup(group)}
                      onClick={e => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      aria-label={`Select all ${group.name}`}
                    />
                    {group.image ? <img src={group.image} alt="" className="h-9 w-9 shrink-0 rounded object-contain" /> : <div className="h-9 w-9 shrink-0 rounded border border-gray-200 bg-white" />}
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold" title={group.name}>{group.name}</span>
                    <span className="hidden text-[11px] text-gray-500 sm:inline">{group.variants.length} variants</span>
                  </div>
                  {group.variants.map(variant => {
                    const canonicalName = `${group.name} - ${variant.name}`;
                    return (
                      <div key={variant.id} className="flex cursor-pointer items-center gap-2 border-t border-gray-100 p-2 pl-7 hover:bg-gray-50" onClick={() => toggleVariant(variant.id)}>
                        <input
                          type="checkbox"
                          checked={selected.includes(variant.id)}
                          onChange={() => toggleVariant(variant.id)}
                          onClick={e => e.stopPropagation()}
                          className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                          aria-label={`Select ${canonicalName}`}
                        />
                        {variant.image || group.image ? <img src={variant.image || group.image} alt="" className="h-8 w-8 shrink-0 rounded object-contain" /> : <div className="h-8 w-8 shrink-0 rounded border border-gray-200 bg-gray-50" />}
                        <div className="min-w-0 flex-1">
                          <span className="inline-flex max-w-full truncate rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-800" title={variant.sku}>{variant.sku}</span>
                          <div className="truncate text-xs text-gray-900" title={canonicalName}>{canonicalName}</div>
                        </div>
                        {showUnitCost && <span className="shrink-0 font-mono text-xs tabular-nums text-gray-700">{formatUnitCost(variant.unitCost)}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-3">
            <span className="text-sm text-gray-500">{selected.length} selected</span>
            <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="inline-flex justify-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Add {selected.length} product{selected.length === 1 ? '' : 's'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProductSelectorModal;
